import { useRef, useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { useLang } from "@/context/LangContext";
import { Monitor, Box, ArrowRight, ArrowDown, Layers, GitBranch, Eye, Server, Info, Zap, Clock, Users, TrendingUp, AlertTriangle, Shield, Maximize2, Minimize2 } from "lucide-react";

function createLabel(text, type, idx, cloudService) {
  const isP = type === "Plane";
  const c = document.createElement("canvas");
  const cx = c.getContext("2d");
  // Tall + narrow so labels don't overlap neighbors but stay highly readable
  c.width = 768; c.height = 600;
  const ac = isP ? "#00F0FF" : "#FFB800";
  cx.fillStyle = "rgba(6,6,12,0.96)"; cx.roundRect(0,0,768,600,22); cx.fill();
  cx.strokeStyle = ac; cx.lineWidth = 4; cx.roundRect(0,0,768,600,22); cx.stroke();
  cx.fillStyle = ac; cx.fillRect(0,0,10,600);
  // Number circle (top-left)
  cx.font = "bold 70px sans-serif"; cx.textAlign = "center"; cx.fillStyle = ac;
  cx.fillText(`${idx+1}`, 70, 100);
  // Type tag
  cx.font = "bold 30px sans-serif"; cx.textAlign = "left"; cx.fillStyle = ac;
  cx.fillText(isP?"SCREEN":"MODUL", 140, 70);
  cx.fillStyle = ac; cx.globalAlpha = 0.3; cx.fillRect(140, 86, 600, 3); cx.globalAlpha = 1;
  // Component name with proper wrapping - very large
  cx.fillStyle = "#FFFFFF"; cx.font = "bold 48px sans-serif";
  const words = text.split(" "); let lines=[], cur="";
  for(const w of words){
    const t=cur?cur+" "+w:w;
    if(cx.measureText(t).width>600){ lines.push(cur); cur=w; }
    else { cur=t; }
  }
  lines.push(cur);
  // Up to 4 lines (one less to leave room for cloud chip)
  const maxLines = cloudService ? 4 : 5;
  lines.slice(0,maxLines).forEach((l,i)=>cx.fillText(l, 140, 180+i*60));
  // Cloud-Service chip (Google Cloud blue tone for enterprise, otherwise muted)
  if (cloudService) {
    const chipY = 460;
    const isEnt = cloudService.tier === "enterprise";
    const chipColor = isEnt ? "#4285F4" : "#888899";
    cx.fillStyle = chipColor; cx.globalAlpha = 0.18;
    cx.roundRect(140, chipY, 580, 56, 12); cx.fill();
    cx.globalAlpha = 1;
    cx.strokeStyle = chipColor; cx.lineWidth = 2;
    cx.roundRect(140, chipY, 580, 56, 12); cx.stroke();
    // Small "GCP" label
    cx.fillStyle = chipColor; cx.font = "bold 22px sans-serif";
    cx.fillText("GCP", 156, chipY + 35);
    // Service short name
    cx.fillStyle = "#FFFFFF"; cx.font = "bold 26px sans-serif";
    cx.fillText(cloudService.short || "-", 220, chipY + 36);
    // Enterprise badge top-right
    if (isEnt) {
      cx.fillStyle = chipColor; cx.font = "bold 16px sans-serif";
      cx.textAlign = "right";
      cx.fillText("ENTERPRISE", 705, chipY + 35);
      cx.textAlign = "left";
    }
  }
  // Footer
  cx.fillStyle = "rgba(200,200,220,0.95)"; cx.font = "26px sans-serif";
  cx.fillText(isP?"Sichtbar fuer den Benutzer":"Laeuft im Hintergrund", 140, 560);
  const tex = new THREE.CanvasTexture(c); tex.needsUpdate = true; tex.anisotropy = 16; return tex;
}

function Scene({ containerRef, visualElements, cam, fullscreen }) {
  useEffect(() => {
    if(!containerRef.current||!visualElements.length) return;
    const el=containerRef.current, W=el.clientWidth, H=el.clientHeight;
    const scene=new THREE.Scene(); scene.background=new THREE.Color("#060610");
    const camera=new THREE.PerspectiveCamera(45,W/H,0.1,100); camera.position.set(...cam.pos);
    const renderer=new THREE.WebGLRenderer({antialias:true, preserveDrawingBuffer:true}); renderer.setSize(W,H); renderer.setPixelRatio(Math.min(window.devicePixelRatio,2));
    el.innerHTML=""; el.appendChild(renderer.domElement);
    const ctrl=new OrbitControls(camera,renderer.domElement); ctrl.enableDamping=true; ctrl.dampingFactor=0.05; ctrl.target.set(...cam.tgt); ctrl.minDistance=3; ctrl.maxDistance=25; ctrl.update();
    scene.add(new THREE.AmbientLight(0xffffff,0.5));
    const l1=new THREE.PointLight(0x00F0FF,1.2,30); l1.position.set(5,8,5); scene.add(l1);
    const l2=new THREE.PointLight(0xFFB800,0.5,20); l2.position.set(-3,6,3); scene.add(l2);
    const grid=new THREE.GridHelper(24,48,0x00F0FF,0x0D0D14); grid.position.set(cam.tgt[0],-1.8,0); grid.material.opacity=0.2; grid.material.transparent=true; scene.add(grid);
    const screens=visualElements.filter(e=>e.type==="Plane"), funcs=visualElements.filter(e=>e.type==="Cube");
    // Scale factor: spread out elements so larger labels don't overlap
    const SX = 1.8, SY = 1.8;
    const px = (e) => e.position[0] * SX;
    const py = (e) => e.position[1] * SY;
    function layerSprite(txt,col,x,y){const c2=document.createElement("canvas");const cx2=c2.getContext("2d");c2.width=1100;c2.height=110;cx2.fillStyle=col;cx2.globalAlpha=0.16;cx2.roundRect(0,0,1100,110,16);cx2.fill();cx2.globalAlpha=1;cx2.fillStyle=col;cx2.font="bold 42px sans-serif";cx2.textAlign="center";cx2.textBaseline="middle";cx2.fillText(txt,550,58);const t2=new THREE.CanvasTexture(c2);t2.needsUpdate=true;t2.anisotropy=16;const s2=new THREE.Sprite(new THREE.SpriteMaterial({map:t2,transparent:true}));s2.position.set(x,y,0.3);s2.scale.set(7,0.7,1);scene.add(s2);}
    if(screens.length){const ax=screens.reduce((s,e)=>s+px(e),0)/screens.length;layerSprite("WAS DER BENUTZER SIEHT (Frontend)","#00F0FF",ax,py(screens[0])-3.7);}
    if(funcs.length){const ax=funcs.reduce((s,e)=>s+px(e),0)/funcs.length;layerSprite("WAS IM HINTERGRUND LAEUFT (Backend)","#FFB800",ax,py(funcs[0])+2.6);}
    const cC=new THREE.Color(0x00F0FF),oC=new THREE.Color(0xFFB800), groups=[];
    visualElements.forEach((ve,i)=>{
      const isP=ve.type==="Plane",col=isP?cC:oC,g=new THREE.Group();g.position.set(px(ve), py(ve), ve.position[2] || 0);
      if(isP){g.add(new THREE.Mesh(new THREE.PlaneGeometry(2.2,1.4),new THREE.MeshBasicMaterial({color:col,wireframe:true,transparent:true,opacity:0.45,side:THREE.DoubleSide})));const f=new THREE.Mesh(new THREE.PlaneGeometry(2.2,1.4),new THREE.MeshBasicMaterial({color:col,transparent:true,opacity:0.05,side:THREE.DoubleSide}));f.position.z=-0.005;g.add(f);const bar=new THREE.Mesh(new THREE.PlaneGeometry(2.2,0.06),new THREE.MeshBasicMaterial({color:col,transparent:true,opacity:0.8,side:THREE.DoubleSide}));bar.position.y=0.7;g.add(bar);}
      else{g.add(new THREE.Mesh(new THREE.BoxGeometry(1.0,1.0,1.0),new THREE.MeshBasicMaterial({color:col,wireframe:true,transparent:true,opacity:0.6})));g.add(new THREE.Mesh(new THREE.BoxGeometry(0.7,0.7,0.7),new THREE.MeshBasicMaterial({color:col,transparent:true,opacity:0.08})));const d=new THREE.Mesh(new THREE.SphereGeometry(0.06,8,8),new THREE.MeshBasicMaterial({color:col}));d.position.set(0.7,0,0);g.add(d);}
      const lt=createLabel(ve.name,ve.type,i,ve.cloud_service);const sp=new THREE.Sprite(new THREE.SpriteMaterial({map:lt,transparent:true}));sp.position.set(0,isP?-2.4:-2.3,0);sp.scale.set(2.6,2.0,1);g.add(sp);
      g.userData={type:ve.type,index:i,origY:py(ve)};groups.push(g);scene.add(g);
    });
    screens.forEach(s=>{funcs.forEach(f=>{if(Math.abs(s.position[0]-f.position[0])<0.8){const pts=[new THREE.Vector3(px(s),py(s)-0.6,0),new THREE.Vector3(px(f),py(f)+0.6,0)];const ge=new THREE.BufferGeometry().setFromPoints(pts);const m=new THREE.LineDashedMaterial({color:0x00F0FF,transparent:true,opacity:0.25,dashSize:0.1,gapSize:0.06});const l=new THREE.Line(ge,m);l.computeLineDistances();scene.add(l);}});});
    const clk=new THREE.Clock();let aid;
    const anim=()=>{aid=requestAnimationFrame(anim);const t=clk.getElapsedTime();groups.forEach(g=>{g.position.y=g.userData.origY+Math.sin(t*0.6+g.userData.index*0.8)*0.03;if(g.userData.type==="Cube"){if(g.children[0]){g.children[0].rotation.y=t*0.2+g.userData.index;g.children[0].rotation.x=Math.sin(t*0.25+g.userData.index)*0.1;}if(g.children[1])g.children[1].rotation.y=t*0.2+g.userData.index;if(g.children[2]){g.children[2].position.x=Math.cos(t*1.2+g.userData.index)*0.5;g.children[2].position.z=Math.sin(t*1.2+g.userData.index)*0.5;}}});ctrl.update();renderer.render(scene,camera);};
    anim();
    const onR=()=>{const w=el.clientWidth,h=el.clientHeight;camera.aspect=w/h;camera.updateProjectionMatrix();renderer.setSize(w,h);};
    window.addEventListener("resize",onR);

    // ---- Snapshot helper: renders an off-screen wide-aspect frame that fits ALL components ----
    // Attached to the container so ProjectDetail.handleExportPDF can call it before
    // capturing the canvas, ensuring no component is cut off in the PDF.
    el.__radarSnapshot__ = (targetW = 1920, targetH = 880) => {
      try {
        // 1) Compute scene bounding box
        const box = new THREE.Box3();
        groups.forEach(g => box.expandByObject(g));
        if (box.isEmpty()) return renderer.domElement.toDataURL("image/png");
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // 2) Save current state
        const prevPos = camera.position.clone();
        const prevTarget = ctrl.target.clone();
        const prevAspect = camera.aspect;
        const prevSize = renderer.getSize(new THREE.Vector2());
        const prevPx = renderer.getPixelRatio();

        // 3) Resize renderer to wide aspect (offscreen) at high DPI
        renderer.setPixelRatio(2);
        renderer.setSize(targetW, targetH, false);
        const aspect = targetW / targetH;
        camera.aspect = aspect;

        // 4) Frame the bounding box with margin
        const fov = camera.fov * (Math.PI / 180);
        const margin = 1.35;
        const distH = (size.y * margin) / (2 * Math.tan(fov / 2));
        const distW = (size.x * margin) / (2 * Math.tan(fov / 2) * aspect);
        const dist = Math.max(distH, distW, 8) + size.z * 0.5 + 2;
        camera.position.set(center.x, center.y + 0.5, dist);
        ctrl.target.copy(center);
        camera.updateProjectionMatrix();
        ctrl.update();

        // 5) Render and capture
        renderer.render(scene, camera);
        const dataUrl = renderer.domElement.toDataURL("image/png");

        // 6) Restore
        camera.position.copy(prevPos);
        ctrl.target.copy(prevTarget);
        camera.aspect = prevAspect;
        camera.updateProjectionMatrix();
        renderer.setPixelRatio(prevPx);
        renderer.setSize(prevSize.x, prevSize.y, false);
        ctrl.update();
        return dataUrl;
      } catch (e) {
        try { return renderer.domElement.toDataURL("image/png"); } catch { return null; }
      }
    };

    return()=>{
      if (el) try { delete el.__radarSnapshot__; } catch { /* ignore */ }
      window.removeEventListener("resize",onR);cancelAnimationFrame(aid);ctrl.dispose();renderer.dispose();scene.clear();
      if(el.contains(renderer.domElement))el.removeChild(renderer.domElement);
    };
  },[containerRef,visualElements,cam,fullscreen]);
  return null;
}

export default function ProjectVisualizer({ visualElements = [], analysis = {} }) {
  const containerRef = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);
  const { t } = useLang();

  const cam = useMemo(() => {
    if (!visualElements.length) return { pos: [3,3,14], tgt: [3,0.5,0] };
    const SX = 1.8, SY = 1.8;
    const xs=visualElements.map(e=>e.position[0]*SX),ys=visualElements.map(e=>e.position[1]*SY);
    const cx=(Math.min(...xs)+Math.max(...xs))/2,cy=(Math.min(...ys)+Math.max(...ys))/2;
    const xr=Math.max(...xs)-Math.min(...xs);
    const yr=Math.max(...ys)-Math.min(...ys);
    const d=Math.max(xr*0.95, (yr+6)*1.1, 9);
    return{pos:[cx,cy+1,d+3.5],tgt:[cx,cy-1,0]};
  },[visualElements]);

  if (!visualElements.length) return null;
  const screens = visualElements.filter(e => e.type === "Plane");
  const functions = visualElements.filter(e => e.type === "Cube");
  const totalHoursMin = analysis?.estimatedHours_min || visualElements.reduce((s,e) => s + (e.hours_min||0), 0);
  const totalHoursMax = analysis?.estimatedHours_max || visualElements.reduce((s,e) => s + (e.hours_max||0), 0);
  const avgHours = Math.round((totalHoursMin + totalHoursMax) / 2);
  const budgetMin = Math.round(totalHoursMin * 75);
  const budgetMax = Math.round(totalHoursMax * 120);
  const complexity = analysis?.projectComplexity || "Mittel";
  const complexityPct = complexity === "Niedrig" ? 25 : complexity === "Mittel" ? 50 : complexity === "Hoch" ? 75 : 95;
  const complexityCol = complexity === "Niedrig" ? "#00F0FF" : complexity === "Mittel" ? "#FFB800" : "#FF3B30";

  return (
    <div data-testid="project-3d-visualizer" className="w-full rounded-xl overflow-hidden border border-white/10 relative" style={{ background: "#060610" }}>

      {/* ========== PROJECT OVERVIEW ========== */}
      <div className="bg-gradient-to-br from-[#00F0FF]/[0.06] via-[#060610] to-[#FFB800]/[0.04] px-6 py-5 border-b border-white/5">
        <div className="grid grid-cols-4 gap-4">
          {/* Complexity Gauge */}
          <div className="glass-surface rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4" style={{ color: complexityCol }} />
              <span className="text-[10px] font-['JetBrains_Mono'] text-[#606070] uppercase tracking-wider">Komplexitaet</span>
            </div>
            <div className="text-2xl font-['Outfit'] font-bold mb-2" style={{ color: complexityCol }}>{complexity}</div>
            <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${complexityPct}%`, background: `linear-gradient(90deg, #00F0FF, ${complexityCol})` }} />
            </div>
            <div className="flex justify-between mt-1"><span className="text-[9px] text-[#606070]">Niedrig</span><span className="text-[9px] text-[#606070]">Sehr Hoch</span></div>
          </div>

          {/* Hours */}
          <div className="glass-surface rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-[10px] font-['JetBrains_Mono'] text-[#606070] uppercase tracking-wider">Aufwand</span>
            </div>
            <div className="text-2xl font-['JetBrains_Mono'] font-bold text-white">{totalHoursMin.toLocaleString("de-DE")}<span className="text-[#606070] text-lg"> – </span>{totalHoursMax.toLocaleString("de-DE")}</div>
            <div className="text-xs text-[#A0A0B0] mt-1">Stunden (ca. {avgHours.toLocaleString("de-DE")}h Mittelwert)</div>
            <div className="text-[10px] text-[#00F0FF] font-['JetBrains_Mono'] mt-1">
              ≈ {Math.round(totalHoursMin / 8).toLocaleString("de-DE")}–{Math.round(totalHoursMax / 8).toLocaleString("de-DE")} Tage
              <span className="text-[#606070] ml-1">(8 h/Tag)</span>
            </div>
          </div>

          {/* Budget */}
          <div className="glass-surface rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-[#FFB800]" />
              <span className="text-[10px] font-['JetBrains_Mono'] text-[#606070] uppercase tracking-wider">Markt-Budget</span>
            </div>
            <div className="text-2xl font-['JetBrains_Mono'] font-bold text-white">{(budgetMin/1000).toFixed(0)}k<span className="text-[#606070] text-lg"> – </span>{(budgetMax/1000).toFixed(0)}k EUR</div>
            <div className="text-xs text-[#A0A0B0] mt-1">Basierend auf 75-120 EUR/h Marktsatz</div>
          </div>

          {/* Components */}
          <div className="glass-surface rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-[10px] font-['JetBrains_Mono'] text-[#606070] uppercase tracking-wider">Umfang</span>
            </div>
            <div className="flex items-end gap-4">
              <div>
                <div className="text-2xl font-['JetBrains_Mono'] font-bold text-[#00F0FF]">{screens.length}</div>
                <div className="text-[10px] text-[#A0A0B0]">UI-Screens</div>
              </div>
              <div>
                <div className="text-2xl font-['JetBrains_Mono'] font-bold text-[#FFB800]">{functions.length}</div>
                <div className="text-[10px] text-[#A0A0B0]">Backend</div>
              </div>
              <div>
                <div className="text-2xl font-['JetBrains_Mono'] font-bold text-white">{visualElements.length}</div>
                <div className="text-[10px] text-[#A0A0B0]">Gesamt</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== INTRO + 3D ========== */}
      <div className="px-5 py-3 bg-[#0A0A12]/60 border-b border-white/5 flex items-center gap-3">
        <Info className="w-4 h-4 text-[#00F0FF] flex-shrink-0" />
        <p className="text-xs text-[#A0A0B0]">
          <span className="text-white font-medium">3D-Architektur-Vorschau:</span>{" "}
          <span className="text-[#00F0FF]">Cyan-Rechtecke</span> = Seiten die der Benutzer sieht |{" "}
          <span className="text-[#FFB800]">Orange-Wuerfel</span> = unsichtbare Backend-Technik |
          Maus: Drehen & Zoomen
        </p>
      </div>
      <div className="relative">
        <div
          ref={containerRef}
          data-testid="project-3d-canvas"
          className="w-full transition-all duration-300"
          style={{ height: fullscreen ? "calc(100vh - 80px)" : "760px" }}
        />
        <button
          data-testid="3d-fullscreen-toggle"
          onClick={() => setFullscreen((p) => !p)}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/60 hover:bg-black/80 border border-white/10 hover:border-[#00F0FF]/40 backdrop-blur-md text-[#A0A0B0] hover:text-[#00F0FF] text-xs font-['Outfit'] font-medium transition-all"
          title={fullscreen ? "Vollbild verlassen" : "Vollbild fuer bessere Lesbarkeit"}
        >
          {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          {fullscreen ? "Verkleinern" : "Vergroessern"}
        </button>
      </div>
      <Scene containerRef={containerRef} visualElements={visualElements} cam={cam} fullscreen={fullscreen} />

      {/* ========== USER JOURNEY ========== */}
      <div className="px-6 py-5 bg-[#080810] border-t border-b border-white/5">
        <div className="flex items-center gap-2 mb-1">
          <GitBranch className="w-4.5 h-4.5 text-[#00F0FF]" />
          <h3 className="font-['Outfit'] text-base font-semibold text-white">Benutzer-Flow: So wird die App genutzt</h3>
        </div>
        <p className="text-[11px] text-[#606070] mb-4 ml-7">Der typische Weg eines Benutzers – vom Oeffnen der App bis zum Ziel</p>
        <div className="flex items-stretch gap-2 flex-wrap ml-7">
          {screens.map((el, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="px-4 py-3 bg-gradient-to-b from-[#00F0FF]/10 to-[#00F0FF]/[0.03] border border-[#00F0FF]/20 rounded-xl min-w-[140px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#00F0FF]/20 text-[11px] font-['JetBrains_Mono'] text-[#00F0FF] font-bold">{i + 1}</span>
                  <span className="text-xs text-[#00F0FF] font-semibold font-['Outfit']">{el.name}</span>
                </div>
                {el.description && <p className="text-[10px] text-[#808090] mt-1 ml-8 leading-relaxed">{el.description}</p>}
              </div>
              {i < screens.length - 1 && <ArrowRight className="w-4 h-4 text-[#00F0FF]/30 flex-shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* ========== DETAILED COMPONENTS ========== */}
      <div className="grid grid-cols-2 gap-0 border-b border-white/5">
        {/* Frontend */}
        <div className="p-6 border-r border-white/5 bg-[#060610]">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#00F0FF]/10">
              <Eye className="w-4 h-4 text-[#00F0FF]" />
            </div>
            <div>
              <h3 className="font-['Outfit'] text-sm font-semibold text-white">Was der Benutzer sieht</h3>
              <p className="text-[10px] text-[#606070]">Seiten, Bildschirme und Interaktionen</p>
            </div>
          </div>
          <div className="space-y-2 mt-4">
            {screens.map((el, i) => (
              <div key={i} className="group px-4 py-3 rounded-xl bg-gradient-to-r from-[#00F0FF]/[0.04] to-transparent border border-[#00F0FF]/10 hover:border-[#00F0FF]/25 transition-all">
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#00F0FF]/15 text-[11px] font-['JetBrains_Mono'] text-[#00F0FF] font-bold flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white font-semibold">{el.name}</span>
                      {el.hours_min && (
                        <span className="text-[10px] font-['JetBrains_Mono'] text-[#00F0FF]/60 whitespace-nowrap">
                          {el.hours_min}-{el.hours_max}h
                          <span className="text-[#606070] ml-1">({Math.round(el.hours_min/8)}-{Math.round(el.hours_max/8)} T.)</span>
                        </span>
                      )}
                    </div>
                    {el.description && <p className="text-[11px] text-[#909098] mt-1 leading-relaxed">{el.description}</p>}
                    {el.cloud_service && (
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`cloud-badge-fe-${i}`}>
                        <span className="text-[9px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider">GCP</span>
                        <span
                          className="text-[10px] px-2 py-0.5 rounded font-['JetBrains_Mono'] font-semibold"
                          style={{
                            background: el.cloud_service.tier === "enterprise" ? "rgba(66,133,244,0.18)" : "rgba(0,240,255,0.10)",
                            color: el.cloud_service.tier === "enterprise" ? "#8AB4F8" : "#00F0FF",
                            border: `1px solid ${el.cloud_service.tier === "enterprise" ? "rgba(66,133,244,0.45)" : "rgba(0,240,255,0.25)"}`,
                          }}
                          title={el.cloud_service.rationale}
                        >
                          {el.cloud_service.short}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Backend */}
        <div className="p-6 bg-[#060610]">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#FFB800]/10">
              <Server className="w-4 h-4 text-[#FFB800]" />
            </div>
            <div>
              <h3 className="font-['Outfit'] text-sm font-semibold text-white">Was im Hintergrund laeuft</h3>
              <p className="text-[10px] text-[#606070]">Server-Logik, Datenbanken, APIs & Integrationen</p>
            </div>
          </div>
          <div className="space-y-2 mt-4">
            {functions.map((el, i) => (
              <div key={i} className="group px-4 py-3 rounded-xl bg-gradient-to-r from-[#FFB800]/[0.04] to-transparent border border-[#FFB800]/10 hover:border-[#FFB800]/25 transition-all">
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#FFB800]/15 text-[11px] font-['JetBrains_Mono'] text-[#FFB800] font-bold flex-shrink-0">{screens.length + i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white font-semibold">{el.name}</span>
                      {el.hours_min && (
                        <span className="text-[10px] font-['JetBrains_Mono'] text-[#FFB800]/60 whitespace-nowrap">
                          {el.hours_min}-{el.hours_max}h
                          <span className="text-[#606070] ml-1">({Math.round(el.hours_min/8)}-{Math.round(el.hours_max/8)} T.)</span>
                        </span>
                      )}
                    </div>
                    {el.description && <p className="text-[11px] text-[#909098] mt-1 leading-relaxed">{el.description}</p>}
                    {el.cloud_service && (
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`cloud-badge-be-${i}`}>
                        <span className="text-[9px] text-[#606070] font-['JetBrains_Mono'] uppercase tracking-wider">GCP</span>
                        <span
                          className="text-[10px] px-2 py-0.5 rounded font-['JetBrains_Mono'] font-semibold"
                          style={{
                            background: el.cloud_service.tier === "enterprise" ? "rgba(66,133,244,0.20)" : "rgba(255,184,0,0.10)",
                            color: el.cloud_service.tier === "enterprise" ? "#8AB4F8" : "#FFB800",
                            border: `1px solid ${el.cloud_service.tier === "enterprise" ? "rgba(66,133,244,0.50)" : "rgba(255,184,0,0.30)"}`,
                          }}
                          title={el.cloud_service.rationale}
                        >
                          {el.cloud_service.short}
                        </span>
                        {el.cloud_service.tier === "enterprise" && (
                          <span className="text-[8px] text-[#8AB4F8] font-['JetBrains_Mono'] uppercase tracking-wider font-bold">
                            Enterprise
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ========== CONNECTION EXPLANATION ========== */}
      <div className="px-6 py-4 bg-gradient-to-r from-[#00F0FF]/[0.03] to-[#FFB800]/[0.03] border-b border-white/5">
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-center gap-1 flex-shrink-0 mt-1">
            <div className="w-3 h-3 rounded-full bg-[#00F0FF]/40" />
            <div className="w-0.5 h-4 bg-white/10" />
            <div className="w-3 h-3 rounded-full bg-[#FFB800]/40" />
          </div>
          <div>
            <h4 className="text-sm font-['Outfit'] font-semibold text-white mb-1">So haengt alles zusammen</h4>
            <p className="text-[11px] text-[#808090] leading-relaxed">
              Jede <span className="text-[#00F0FF]">Seite</span> (Cyan, oben) sendet Anfragen an die <span className="text-[#FFB800]">Backend-Module</span> (Orange, unten).
              Beispiel: Wenn ein Benutzer einen <span className="text-white">Termin erstellt</span>, sendet die "Meeting & Raum-Buchung"-Seite die Daten an das "Kalender-Backend",
              das prueft ob der Raum frei ist und speichert den Termin. Danach benachrichtigt das "Benachrichtigungs-System" alle Teilnehmer per E-Mail und Push.
              Die gestrichelten Linien in der 3D-Ansicht zeigen diese Verbindungen.
            </p>
          </div>
        </div>
      </div>

      {/* ========== TECH STACK ========== */}
      {analysis?.techStack_reco?.length > 0 && (
        <div className="px-6 py-4 bg-[#080810] border-b border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[#00F0FF]" />
            <span className="font-['Outfit'] text-sm font-semibold text-white">Empfohlene Technologien</span>
          </div>
          <div className="flex flex-wrap gap-2 ml-6">
            {analysis.techStack_reco.map((tech, i) => (
              <span key={i} className="px-3 py-1.5 bg-[#00F0FF]/5 border border-[#00F0FF]/15 text-[#00F0FF] text-xs font-['JetBrains_Mono'] rounded-lg">{tech}</span>
            ))}
          </div>
        </div>
      )}

      {/* ========== TEAM SIZE RECOMMENDATION ========== */}
      <div className="px-6 py-4 bg-[#060610] border-b border-white/5">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-[#00F0FF]" />
          <span className="font-['Outfit'] text-sm font-semibold text-white">Empfohlene Teamgroesse</span>
        </div>
        <div className="ml-6 flex items-center gap-6">
          <div>
            <span className="text-xl font-['JetBrains_Mono'] font-bold text-white">{screens.length <= 3 ? "1-2" : screens.length <= 6 ? "2-4" : "4-6"}</span>
            <span className="text-xs text-[#A0A0B0] ml-2">Entwickler</span>
          </div>
          <div className="h-8 w-px bg-white/10" />
          <div>
            <span className="text-xl font-['JetBrains_Mono'] font-bold text-white">1</span>
            <span className="text-xs text-[#A0A0B0] ml-2">Projektmanager</span>
          </div>
          <div className="h-8 w-px bg-white/10" />
          <div>
            <span className="text-xl font-['JetBrains_Mono'] font-bold text-white">1</span>
            <span className="text-xs text-[#A0A0B0] ml-2">UI/UX Designer</span>
          </div>
        </div>
      </div>
    </div>
  );
}
